/**
 * TenantTree — sdílený komponent pro hierarchický strom tenantů.
 * Použit v Identity (přístupy) i Mesh (infrastruktura).
 */
import { useMemo, useState } from 'react';
import { Building2, ChevronRight, Users } from 'lucide-react';
import { Badge } from './ui/Badge';
import { useTranslation } from 'react-i18next';

export interface TenantFlat {
  id: string;
  slug: string;
  name: string;
  plan: string;
  parent_id: string | null;
  node_count: number;
  enabled: boolean;
  /** Vlastník uzlu (schválený Root CA otisk) — kořen struktury. */
  owner?: boolean;
  /** `outside` = uzel tenanta zná jen jako vazbu (přes hub), není to jeho organizace. */
  scope?: 'organization' | 'outside';
}

export interface TenantNode extends TenantFlat {
  children: TenantNode[];
}

export function buildTenantTree(flat: TenantFlat[]): TenantNode[] {
  const map = new Map(flat.map((t) => [t.id, { ...t, children: [] as TenantNode[] }]));
  const roots: TenantNode[] = [];
  for (const t of map.values()) {
    if (t.parent_id && map.has(t.parent_id)) {
      map.get(t.parent_id)!.children.push(t);
    } else {
      roots.push(t);
    }
  }
  return roots;
}

interface TenantTreeProps {
  tenants: TenantFlat[];
  selected: string | null;
  onSelect: (id: string | null) => void;
  summaries?: Record<string, { group_count: number; member_count: number; key_count: number }>;
  showRootOrg?: boolean;
}

export function TenantTree(
  { tenants, selected, onSelect, summaries, showRootOrg = true }: TenantTreeProps,
) {
  const { t } = useTranslation();
  // Struktura organizace vs. vazby mimo ni — kreslit je vedle sebe jako dva
  // kořeny byl ten „nesrozumitelný strom" (Filip 2026-10-14, Apollo).
  const inside = useMemo(() => tenants.filter((x) => x.scope !== 'outside'), [tenants]);
  const outside = useMemo(() => tenants.filter((x) => x.scope === 'outside'), [tenants]);
  const tree = useMemo(() => buildTenantTree(inside), [inside]);
  const ownerName = tenants.find((x) => x.owner)?.name ?? t('tenanttree.organizace', 'Organizace');

  return (
    <div className='space-y-0.5'>
      {showRootOrg && (
        <button
          onClick={() => onSelect(null)}
          className={`w-full text-left p-2.5 rounded-lg text-xs flex items-center gap-2 transition-colors ${
            selected === null
              ? 'bg-blue-950/40 border border-blue-500/30'
              : 'hover:bg-gray-800/40 border border-transparent'
          }`}
        >
          <Building2 size={14} className='text-amber-400' />
          <span className='font-semibold text-white'>{ownerName}</span>
          <span className='text-[10px] text-gray-500 ml-auto'>{inside.length} {t('tenanttree.tenantu', 'tenantů')}</span>
        </button>
      )}
      {tree.map((node) => (
        <TenantTreeItem
          key={node.id}
          node={node}
          depth={showRootOrg ? 1 : 0}
          selected={selected}
          onSelect={onSelect}
          summaries={summaries}
        />
      ))}
      {outside.length > 0 && (
        <div className='pt-2'>
          <div className='px-2 pb-1 text-[10px] uppercase tracking-wide text-gray-500'>
            {t('tenanttree.mimoOrganizaci', 'Mimo organizaci — vazby přes hub')}
          </div>
          {buildTenantTree(outside).map((node) => (
            <TenantTreeItem
              key={node.id}
              node={node}
              depth={showRootOrg ? 1 : 0}
              selected={selected}
              onSelect={onSelect}
              summaries={summaries}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TenantTreeItem({
  node,
  depth,
  selected,
  onSelect,
  summaries,
}: {
  node: TenantNode;
  depth: number;
  selected: string | null;
  onSelect: (id: string) => void;
  summaries?: Record<string, { group_count: number; member_count: number; key_count: number }>;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children.length > 0;
  const summary = summaries?.[node.id];
  const isSelected = selected === node.id;

  return (
    <div>
      <button
        onClick={() => onSelect(node.id)}
        className={`w-full text-left p-2 rounded-lg text-xs flex items-center gap-2 transition-colors ${
          isSelected
            ? 'bg-blue-950/40 border border-blue-500/30'
            : 'hover:bg-gray-800/40 border border-transparent'
        }`}
        style={{ paddingLeft: depth * 20 + 8 }}
      >
        {hasChildren
          ? (
            <span
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
              className='p-0.5 hover:bg-gray-700 rounded cursor-pointer'
            >
              <ChevronRight
                size={12}
                className={`transition-transform ${expanded ? 'rotate-90' : ''}`}
              />
            </span>
          )
          : <span className='w-4' />}
        <Users size={13} className={node.enabled ? 'text-blue-400' : 'text-gray-600'} />
        <span className='font-medium text-white'>{node.name}</span>
        <Badge className='text-[9px]'>
          {node.plan}
        </Badge>
        <span className='text-[10px] text-gray-500 ml-auto flex gap-3'>
          {node.node_count > 0 && <span>{node.node_count} {t('tenanttree.nodu', 'nodů')}</span>}
          {summary && <span>{summary.group_count} skupin</span>}
          {summary && <span>{summary.key_count} {t('tenanttree.klicu', 'klíčů')}</span>}
        </span>
      </button>
      {expanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <TenantTreeItem
              key={child.id}
              node={child}
              depth={depth + 1}
              selected={selected}
              onSelect={onSelect}
              summaries={summaries}
            />
          ))}
        </div>
      )}
    </div>
  );
}
